import { produce } from "immer";
import { SortOrder } from "../contract/pam.js";
import { getNHoursAgoAsSeconds, endDateNow } from "../utils/date.js";
import "react-intl";
import { BaseRequestBuilder } from "./BaseRequestBuilder.js";
class GqlRequestBuilder extends BaseRequestBuilder {
  buildRequest(requestPayload) {
    const filter = this.mapToFilterObject(requestPayload.filter);
    let response = null;
    if (!this.isFilterContainInvalidValue(filter)) {
      this.validateFilters(filter);
      response = {
        query: this.buildQuery(requestPayload.fields),
        variables: this.getGqlPageArgs(filter)
      };
    }
    return response;
  }
  buildQueryFields(fields) {
    const tree = this.buildQueryFieldTree([...new Set(fields?.filter((i) => i))]);
    return this.formatQueryFieldTree(tree);
  }
  buildQueryFieldTree(queryFields) {
    return produce({}, (draft) => {
      queryFields?.forEach((queryField) => {
        let cursor = draft;
        const fields = queryField.split(".");
        fields.forEach((field) => {
          if (!cursor[field]) {
            cursor[field] = {};
          }
          cursor = cursor[field];
        });
      });
    });
  }
  formatQueryFieldTree(tree, nestingLevel = 0) {
    let result = "";
    const tab = "  ";
    const currentSpacing = tab.repeat(nestingLevel);
    Object.keys(tree).forEach((field) => {
      if (Object.keys(tree[field]).length > 0) {
        result = `${result}${currentSpacing}${field} {
${this.formatQueryFieldTree(
          tree[field],
          nestingLevel + 1
        )}${currentSpacing}}
`;
      } else {
        result = `${result}${currentSpacing}${field}
`;
      }
    });
    return result;
  }
  /**
   * @deprecated
   * <p>Use only for old queries, until they are migrated to building fields by VM keys with {@link buildQueryFields}</p>
   */
  hasQueryItem(fields, field) {
    return fields.includes(field) ? "" : " @client ";
  }
  /**
   * @deprecated
   * <p>Use only for old queries, until they are migrated to building fields by VM keys with {@link buildQueryFields}</p>
   */
  hasAnyOfQueryItems(fields, subFields) {
    return fields.some((r) => subFields.includes(r)) ? "" : " @client ";
  }
  /**
   * @deprecated
   * <p>Use {@link buildQueryFields}. For old queries, use {@link hasQueryItem}</p>
   */
  hasField(actualFields, expectedField) {
    return actualFields.includes(expectedField);
  }
  /**
   * @deprecated
   * <p>Use {@link buildQueryFields}</p>
   */
  hasAnyField(actualFields, expectedFields) {
    return actualFields.some((r) => expectedFields.includes(r));
  }
  getGqlPageArgs(filter) {
    const paging = this.hasPagination() ? this.buildPaging(filter) : {};
    return filter ? { ...this.buildFilter(filter), ...this.buildSort(filter), ...paging } : paging;
  }
  toGQLObjectFilter(filter, filterName) {
    return this.toObjectFilter(filter, filterName);
  }
  toGQLStringFilter(filter, filterName) {
    return this.toStringFilter(filter, filterName);
  }
  toGQLBooleanFilter(filter, filterName) {
    return this.toBooleanFilter(filter, filterName);
  }
  toGQLMultiselectFilter(filter, filterName, isArrayInString) {
    return this.toMultiselectFilter(filter, filterName, isArrayInString);
  }
  getGQLTextFilter(textFilter) {
    textFilter = textFilter.filter((f) => f?.text?.length);
    return textFilter.length ? textFilter : void 0;
  }
  toGQLDateRange(from, to) {
    const getDateFilterPart = (t) => {
      const seconds = Number(t);
      const result = isNaN(seconds) || !seconds ? null : seconds;
      return t ? t === endDateNow ? { seconds: getNHoursAgoAsSeconds(0) } : { seconds: result } : null;
    };
    return from || to ? {
      from: getDateFilterPart(from),
      to: getDateFilterPart(to)
    } : void 0;
  }
  toGQLNumberRangeFilter(min, max) {
    const getNumberRangeValue = (num) => {
      const value = Number(num);
      return isNaN(value) || value === null || value === void 0 ? null : value;
    };
    return min || max ? {
      min: getNumberRangeValue(min),
      max: getNumberRangeValue(max)
    } : void 0;
  }
  toGQLTextFilter(fields, value, transformTextMapper) {
    if (fields?.length && value) {
      let parsedValue;
      try {
        parsedValue = isNaN(Number(value)) ? JSON.parse(value) : value;
        parsedValue = Array.isArray(parsedValue) ? parsedValue.join(" ") : parsedValue;
      } catch {
        parsedValue = value;
      }
      return {
        text: transformTextMapper ? transformTextMapper(parsedValue?.toString()) : parsedValue?.toString(),
        search_in: fields
      };
    } else {
      return null;
    }
  }
  ignoreCase(value) {
    const isSymbol = (str) => {
      return !!str.match(/[a-z!@#$%^&()_+=[\]{};':"\\|,.<>/?]/i);
    };
    const getCaseInsensitiveWord = (str) => {
      return str.split("").map((c) => isSymbol(c) ? `[${c.toUpperCase()}${c.toLowerCase()}]` : c).join("");
    };
    return value.toString().split(" ").map((i) => `/${getCaseInsensitiveWord(i)}/`).join(" ").replaceAll("*", ".*");
  }
  phraseSearch(value) {
    return `"${value}"`;
  }
  phraseListSearch(value, separator) {
    return value?.length ? value.split(separator).map((v) => `"${v}"`).join(separator) : value;
  }
  buildSort(filter) {
    return filter.sortOrder || filter.sortField ? {
      sort: {
        sort: filter.sortField,
        order: [SortOrder.Asc, "asc"].includes(filter.sortOrder) ? SortOrder.Asc : SortOrder.Desc
      }
    } : {};
  }
  //TODO: [BO-2912] Refactor sorting interfacte in GqlRequestBuilder to array of objects instead of 2 separate strings
  buildMultipleSort(filter) {
    if (filter.sortOrder || filter.sortField) {
      const sortFields = filter.sortField?.split(",");
      const sortOrders = filter.sortOrder?.split(",");
      return {
        sort: sortFields?.map((sortField, index) => ({
          sort: sortField,
          order: sortOrders?.[index] === "asc" ? SortOrder.Asc : SortOrder.Desc
        }))
      };
    } else return {};
  }
  hasPagination() {
    return true;
  }
  getLatestTimestamp(fromTimestamp, minTimestamp) {
    return !minTimestamp || fromTimestamp && fromTimestamp > minTimestamp ? fromTimestamp : minTimestamp;
  }
}
export {
  GqlRequestBuilder
};
//# sourceMappingURL=GqlRequestBuilder.js.map
